import { Factory } from "@/data/dao/Factory";
import { type IOfflineStorage, OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { config } from "@/data/util/Config";

import { globalStores } from "@/app/globalStores";

export class ApplicationModeService {
    private static instance: ApplicationModeService;

    public static getInstance() {
        if (ApplicationModeService.instance === undefined) {
            ApplicationModeService.instance = new ApplicationModeService();
        }
        return ApplicationModeService.instance;
    }

    public initialize(mode: "kiosk" | "normal" | undefined, showScreenSaver: boolean) {
        if (mode === "kiosk") {
            this.setKioskMode(showScreenSaver);
            console.log("Kiosk mode enabled");
        } else if (mode === "normal") {
            this.setNormalMode();
            console.log("Normal application mode enabled");
        } else {
            this.loadPreviousMode(showScreenSaver);
        }
    }

    public setKioskMode(showScreenSaver: boolean = true) {
        globalStores.kioskStore!.isKioskMode = true;
        if (showScreenSaver) {
            globalStores.kioskStore!.showScreenSaver();
        }

        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        offlineCache.write(config.appModeCookieName, "kiosk");
    }

    public setNormalMode() {
        globalStores.kioskStore!.isKioskMode = false;

        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        offlineCache.remove(config.appModeCookieName);
    }

    public getKioskMode(): boolean {
        return globalStores.kioskStore!.isKioskMode;
    }

    // a kiosk terminal has to find its location again after a restart, and by then the address
    // bar can be a bare "/" and the session cookie can be gone. the permalink code is the whole
    // address, kept in the store the mode uses - a cookie backed by local storage - and written
    // on every kiosk allocation, so it also follows a location that gets a new code
    public setKioskPermalinkCode(code: string) {
        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        offlineCache.writeString(config.kioskCodeCookieName, code);
    }

    public getKioskPermalinkCode(): string | undefined {
        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        return offlineCache.readString(config.kioskCodeCookieName) ?? undefined;
    }

    public setPreviousMode(mode: "kiosk" | "normal") {
        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        offlineCache.write(config.appModeCookieName, mode);
    }

    public getPreviousMode(): "kiosk" | "normal" | undefined {
        const offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        const previousMode = offlineCache.read(config.appModeCookieName);
        if (previousMode === "kiosk") {
            return "kiosk";
        } else {
            return "normal";
        }
    }

    public loadPreviousMode(showScreenSaver: boolean = true) {
        const previousMode = this.getPreviousMode();
        if (previousMode === "kiosk") {
            this.setKioskMode(showScreenSaver);
            console.log("Previous kiosk mode enabled");
        } else {
            this.setNormalMode();
            console.log("Previous normal mode enabled");
        }
    }
}

export function setApplicationMode(mode: "kiosk" | "normal" | undefined, showScreenSaver: boolean) {
    ApplicationModeService.getInstance().initialize(mode, showScreenSaver);
}
